/* istanbul ignore file */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { observable, reaction } from 'mobx';
import once from 'lodash/once';
import debounce from 'lodash/debounce';
import { getQuery } from 'steamer-browserutils';
import DUISnackbar from '@tencent/dui/lib/components/Snackbar';
import { getMeetingID } from '@src/utils/tencent-meeting';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import i18n from '@i18next/i18n';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { MutationSource } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IEditingPresentManager } from '@src/slide/application/service/editing-present-manager/core/editing-present-manager.interface';
import EditingPresent, { BizType, EditingPresentRole, HostPresentInfo, PresentInfo } from './editing-present';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';

export { EditingPresentRole };

enum RetCode {
    SUCCESS = 0,

    /** 修改标题 - 标题不合规 */
    NON_COMPLIANCE_TITLE = 12000,

    // 编辑态下的远程演示(即跟随)
    /** 该文档已经被会议占用 */
    DOC_HAS_BEEN_USED = 64000,
    /** 演示信息不存在，视为正常 */
    EMPTY_PRESENT_INFO = 64003,
    /** 主讲人为空, 视为正常 */
    HOST_NOT_EXIST = 64004,
}


// 改变 currentSlide 的操作分为:
// 1. select 操作
//  a. 画布区域滚动滚轮
//  b. 侧边栏区域上下方向键
//  c. 侧边栏区域鼠标选中切换
//  d. 搜索能力跳转页面
// 这四种操作会直接 update slideIndex
// 2. 另外，slideList 是一个简单的链表，对应的 node 操作 (三种 add,delete,move) 也将改变选中项
//  a. add (包含 paste): 在当前选中的页后插入新页并选中
//  b. delete (包含 cut): 删除当前选中页并选中下一页（没有下一页时选中上一页
//  c. move: 移动 node 并变更选中 index
// slideList + slideIndex 都会变更，且是独立的两个原子操作，因此如果监听 currentSlide.id 将触发两次

const DEBOUNCE_DELAY_MS = 300;


/** 从 URL 中获取初始角色 */
function getInitRole() {
    // 主讲人标识 一期定义在 sc 组件/liveComponent 中的 magic string
    return getQuery('login') === 'speaker'
        ? EditingPresentRole.host
        : EditingPresentRole.audience;
}

function showTips() {
    DUISnackbar.show({
        message: i18n.t('你可以自由浏览此文档'),
        type: 'success',
        autoClose: true,
    });
}

const showTipsOnce = once(showTips);

/**
 * 编辑态下的远程演示(编辑态跟随)
 * 跟 ppt 演示态(live)类似，即在编辑态中广播主讲人的页面信息给所有参会者，参会者可选择跟随页面或自由编辑
 * NOTE: 目前涉及功能不多，主讲人/参会者暂时放在一个类中
 * TODO:
 * 1. 拆分主讲人/参会者逻辑
 * 2. 时序问题：对于参会者，xhr（getPresentInfo) 到 socket 建联完成 期间的切页消息是丢失的；后者完成后，应重发一次 xhr fetchMissing
 */
class EditingPresentManager implements IEditingPresentManager {
    /** 用户角色 */
    @observable
    public role?: EditingPresentRole;

    /** [参会者] 是否开启跟随 */
    @observable
    public isPresentFollowing = true;

    /** [参会者] 主讲人头像 */
    public hostAvatar: string;

    /**
     * [参会者] 主讲人 slideId
     * 1. 只有初始时可能为空，初始化后永远有值
     * 2. **若未初始化，表现上应视为主讲人未进会**
     */
    @observable
    public hostSlideId: string;

    /** [参会者] 候选 slideId */
    private candidateHostSlideId: string | null = null;

    /** [参会者] 退出跟随事件注册标识 */
    private hasRegisterEndFollowEvent = false;

    private editingPresent: EditingPresent;

    private debouncedBroadcast = debounce((slideId: string) => {
        if (!slideId) return;

        this.editingPresent.hostBroadcast({
            slideId,
            avatar: SlideDataServices.ClientVarsService()?.getClientVars().userPic ?? '',
        });
        window.log.info(`[EditingPresent] [host] slideId change to ${slideId}`);
    }, DEBOUNCE_DELAY_MS);

    public constructor(private selectionService: ISelectionService) {
        this.init();
    }

    public async init() {
        SlideDataServices.SDKDataServices.EventManager.promise()
            .then(({ socketEvents }) => {
                socketEvents.onPresentInfo(this.onReceivePresentInfo);
            });

        // 通过会议 sdk 获取会议 id，获取失败则尝试从 url 中取值
        const bizId = await getMeetingID() ?? getQuery('editingPresentId');
        if (!bizId) return;

        const [clientVarService, slideAppService, Collaboration, apiService] = await Promise.all([
            SlideDataServices.ClientVarsService.promise(),
            SlideDataServices.SlideAppService.promise(),
            OnlineEditServices.CollaborationManager.promise(),
            OnlineEditServices.OnlineApiService.promise(),
        ]);
        await slideAppService.onReadyPromise();
        this.editingPresent = new EditingPresent(Collaboration.getSocket(), clientVarService, apiService, this);
        let presentInfo: PresentInfo | null = null;
        try {
            presentInfo = await this.editingPresent.init({
                biztype: BizType.meeting,
                bizid: bizId,
                initRole: getInitRole(),
            });
        } catch (error) {
            window.log.error('[EditingPresent] error whilie init', error);

            if (error.retCode === RetCode.DOC_HAS_BEEN_USED) {
                DUISnackbar.show({
                    message: i18n.t('该文档已被其他会议占用，无法开启跟随模式'),
                    type: 'info',
                    autoClose: true,
                });

                return;
            }

            DUISnackbar.show({
                message: i18n.t('未知错误 {0}，无法开启跟随模式', { 0: error.retCode }),
                type: 'info',
                autoClose: true,
            });
        }

        if (!presentInfo) return;

        this.role = presentInfo.role;
        if (this.role === EditingPresentRole.host) {
            this.initHost(presentInfo.editPos?.slideId);
        } else {
            this.initAudience(presentInfo.editPos);
        }
    }

    /** [参会者] 开始跟随 */
    public startPresentFollow() {
        this.isPresentFollowing = true;

        const selection = this.selectionService;
        // 清除 editor element 选中状态
        selection?.selectedElements.clear();
        // 重置最后选中区域
        if (selection) {
            selection.lastMouseDownRegion = RegionType.NONE;
        }

        this.updateCurrentSlideByStore();
    }

    /** [参会者] 结束跟随 */
    public endPresentFollow() {
        this.isPresentFollowing = false;
        showTipsOnce();
    }

    /** [参会者] 获取自身及主讲人页数 */
    public getSlideIndex() {
        const hostSlideIndex = this.findSlideIndex(this.hostSlideId);

        // 1. [PC] 只有 编辑 状态, `selectionService.currentSlideIndex` 为当前编辑页面
        // 2. [mobile] 移动分为 查看（列表） / 编辑 两个状态，<编辑>时当前页同上，<列表>时当前页码为
        const localSlideIndex = browser.isMobile
            ? this.selectionService?.mobileCurrentDisplayIndex
            : this.selectionService?.currentSlideIndex;

        return {
            // 不存在时视为第一页
            hostSlideIndex: Math.max(0, hostSlideIndex),
            localSlideIndex: localSlideIndex === undefined ? -1 : localSlideIndex,
        };
    }

    public onReceivePresentInfo = (hostPresentInfo: HostPresentInfo) => {
        window.log.info(`[EditingPresent] [audience] receive notice ${JSON.stringify(hostPresentInfo)}`);
        if (!hostPresentInfo) return;

        this.hostAvatar = hostPresentInfo.avatar;
        this.setHostSlideId(hostPresentInfo.slideId);
    };

    /** [主讲人] 初始化 */
    private initHost(lastSlideId?: string) {
        if (lastSlideId) {
            // 尝试恢复自己上一次的广播状态
            this.switchToSlideById(lastSlideId);
        } else {
            // 否则补一次广播
            const id = this.selectionService?.currentSlide?.id;
            if (id) {
                this.debouncedBroadcast(id);
            }
        }

        // 注意特殊 case：所有页面均被删除，此时 currentSlide 为空，需要防御
        if (browser.isMobile) {
            reaction(() => this.selectionService?.mobileCurrentDisplayIndex, (slideIndex: number) => {
                const slide =  this.selectionService?.slides[slideIndex];
                if (slide?.id) {
                    this.debouncedBroadcast(slide.id);
                }
            });
        } else {
            reaction(() => this.selectionService?.currentSlide?.id, this.debouncedBroadcast);
        }
    }

    // TODO: 以下涉及若干个全局事件的监听，目前事件名没有统一管理，待收集后干掉这些 magic string；

    /** [参会者] 初始化 */
    private initAudience(hostPresentInfo?: HostPresentInfo) {
        if (hostPresentInfo) {
            this.hostAvatar = hostPresentInfo.avatar;
            // 已有主讲人但页码消息未至，默认为第一页；
            const initSlideId = hostPresentInfo.slideId ?? this.selectionService?.slides[0]?.id;
            this.setHostSlideId(initSlideId);
        }

        // 页操作有可能同时包括了 数据变更 & 切页，如以下两种情况
        // 1. 任何一人删除主讲人所在页；（页删除 + 切换到相邻页）
        // 2. 主讲人新增页面；（在当前页后面新增一页 + 切换到该页）
        // 数据变更 & 切页消息的时序是不确定的，需要容错；如 2，若切页消息先于数据消息到达，则消息间隔内 hostSlideId 是不存在的；
        // 因此在此处监听 slide 列表长度代替数据变更事件，数据变更时检查此前是否有未更新成功的切页消息；
        reaction(() => this.selectionService?.slides.length, () => {
            if (!this.candidateHostSlideId) return;

            this.setHostSlideId(this.candidateHostSlideId);
        });
    }

    /** [参会者] 设置主讲人 slideId */
    private setHostSlideId(slideId: string) {
        // slideId 来自 socket 消息，多做一层防御
        if (typeof slideId !== 'string' || !slideId) return;

        // 数据消息晚于切页消息抵达时，slideId 在当前列表中不存在；详见上方注释
        // 如：主讲人新增页面同时会切页，若切页消息先到，数据消息后到，则需要兼容
        if (this.findSlideIndex(slideId) === -1) {
            // 暂存 slideId，等待 slide 长度变化时再尝试消费；
            this.candidateHostSlideId = slideId;
            return;
        }

        this.hostSlideId = slideId;
        // set 成功

        // 清除暂存的 slideId
        this.candidateHostSlideId = null;
        // 跟随主讲人翻页
        if (this.isPresentFollowing) this.updateCurrentSlideByStore();
        // 首次 set 后，注册退出跟随事件
        // NOTE: 只有当 slideId 被初始化后，才认为主讲人已经进会；此时再注册参会者事件
        if (!this.hasRegisterEndFollowEvent) this.registerEndFollowEvent();
    }

    /** [参会者] 注册退出跟随相关事件 */
    private registerEndFollowEvent() {
        this.hasRegisterEndFollowEvent = true;

        SlideDataServices.SDKDataServices.EventManager.promise().then(({ mutationEvents }) => {
            mutationEvents.onCommitCommand((mutationSource) => {
                if ([MutationSource.Editor, MutationSource.UndoRedo].includes(mutationSource)) {
                    if (!this.isPresentFollowing) return;

                    window.log.info('[EditingPresent] [audience] 参会者主动发起变更, 退出跟随');
                    this.endPresentFollow();
                }
            });
        });

        // 监听用户选中区域变更, 当选中 editor 元素 或 选中备注区域时，退出跟随
        reaction(() => this.selectionService?.lastMouseDownRegion, (lastMouseDownRegion) => {
            if (lastMouseDownRegion !== RegionType.EDITOR && lastMouseDownRegion !== RegionType.NOTE) return;
            if (!this.isPresentFollowing) return;

            window.log.info(`[EditingPresent2] [audience] 参会者选中区域变更 ${lastMouseDownRegion}, 退出跟随`);
            this.endPresentFollow();
        });

        // NOTE：参会者需要识别 slideIndex 的变更是否是用户主动触发；而主讲人不论主动/被动变更都广播事件
        window.addEventListener('onCurrentSlideIndexChangeByUserAction', () => {
            if (!this.isPresentFollowing) return;

            window.log.info('[EditingPresent] [audience] 参会者主动切换页面, 退出跟随');
            this.endPresentFollow();
        });
    }

    /** [参会者] 刷新当前页码 */
    private updateCurrentSlideByStore() {
        window.log.info(`[EditingPresent] [audience] updateCurrentSlideByStore ${this.hostSlideId}`);

        if (this.hostSlideId) this.switchToSlideById(this.hostSlideId);
    }

    /**
     * 根据 slideid 切页
     * 1. [PC] 变更 selectionService.currentSlideIndex，通过数据驱动更新 ui
     * 2. [mobile] 移动分为 只读 / 编辑 两个状态，其中只读态的 {当前页} 数据源有差异 selectionService.mobileCurrentDisplayIndex
     *    且 ui 层无法通过数据驱动；因此这里只派发事件，由 ui 层响应事件并滚动到指定位置
     *
     * TODO: 当前方法不够通用，待考虑清楚后再提取到 slideStore 中作为公共方法
     */
    private switchToSlideById(slideId: string) {
        const index = this.findSlideIndex(slideId);
        if (index === -1) return false;

        if (browser.isMobile) {
            // scrollSlideIntoViewByIndexMobile 事件在 src/framework/frame-m/components/list/index.js#133 中监听
            window.dispatchEvent(new CustomEvent('scrollSlideIntoViewByIndexMobile', {
                detail: {
                    slideIndex: index,
                },
            }));
        } else {
            const selection = this.selectionService;
            if (selection?.currentSlideIndex !== index) {
                // 不主动 clear 会导致选中多张 slide，api 设计似乎有些问题
                selection?.selectedSlides.clear();
                if (selection) {
                    selection.currentSlideIndex = index;
                }
            }
        }

        return true;
    }

    private findSlideIndex(slideId: string): number {
        const selection = this.selectionService;
        if (!selection) return -1;
        return selection.slides.findIndex(slide => slide.id === slideId);
    }
}

export default EditingPresentManager;
